import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { searchGraphUsers, GraphUser } from '../../../../../../utils/MsGraphApiCall';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { updateAssignment } from '../../../../state';

// True only for legacy records (persisted before defaultAssigneeName existed) not yet resolved.
const needsGraphResolution = (email: string | undefined, name: string | undefined, options: GraphUser[]): email is string =>
  !!email && !name && !options.some(u => u.mail?.toLowerCase() === email.toLowerCase());

// Resolves a legacy record's display name via Graph and appends it to the option list.
const resolveLegacyAssigneeName = (
  email: string,
  setOptions: React.Dispatch<React.SetStateAction<GraphUser[]>>
): (() => void) => {
  let cancelled = false;
  searchGraphUsers(email)
    .then(users => {
      const match = users.find(u => u.mail?.toLowerCase() === email.toLowerCase());
      if (match && !cancelled) {
        setOptions(prev => prev.some(u => u.mail === match.mail) ? prev : [...prev, match]);
      }
    })
    .catch(error => console.error('Error resolving default assignee:', error));
  return () => { cancelled = true; };
};

// Builds a placeholder GraphUser from a stored assignment, tolerating legacy records that
// only ever persisted the email.
const buildPlaceholderUser = (defaultAssignee: string, defaultAssigneeName: string): GraphUser => ({
  displayName: defaultAssigneeName || defaultAssignee,
  mail: defaultAssignee,
  id: '',
  userPrincipalName: defaultAssignee,
  accountEnabled: true
});

const DefaultAssigneeEdit: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentRecurringTask } = useAppSelector(state => state.recurringTask);
  const [options, setOptions] = useState<GraphUser[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (searchText: string) => {
    if (searchText.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const users = await searchGraphUsers(searchText);
      setOptions(users);
    } catch (error) {
      console.error('Error searching users:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // The email is always persisted; the display name is persisted too when known,
  // falling back to a live Graph lookup only for records saved before that field existed.
  useEffect(() => {
    const email = currentRecurringTask?.defaultAssignee;
    const name = currentRecurringTask?.defaultAssigneeName;
    if (!needsGraphResolution(email, name, options)) return;
    return resolveLegacyAssigneeName(email, setOptions);
  }, [currentRecurringTask?.defaultAssignee, currentRecurringTask?.defaultAssigneeName]);

  const handleChange = (_: any, value: GraphUser | null) => {
    dispatch(updateAssignment({
      defaultAssignee: value?.mail || '',
      defaultAssigneeName: value?.displayName || ''
    }));
  };

  // Create a placeholder GraphUser when loading saved data
  const currentValue = options.find(u => u.mail === currentRecurringTask?.defaultAssignee) ||
    (currentRecurringTask?.defaultAssignee
      ? buildPlaceholderUser(currentRecurringTask.defaultAssignee, currentRecurringTask.defaultAssigneeName)
      : null);


  return (
    <Autocomplete
      size="small"
      options={options}
      getOptionLabel={(option) => option.displayName}
      value={currentValue}
      onChange={handleChange}
      onInputChange={(_, value) => handleSearch(value)}
      loading={loading}
      sx={{ minWidth: 250 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Default Assignee"
          placeholder="Search for user..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default DefaultAssigneeEdit;
